// The login flow, as a state machine over the two endpoints the service already
// has.
//
// NO SECOND LOGIN MECHANISM. There is one credential and one rotation schedule:
// ask for a code at an address, spend the code for a session, and present the
// session as a bearer token. Nothing in this package issues, stores or verifies
// anything else, and the token is the same auth_session row the report surface
// reads. api/src/reports/auth.ts is explicit about what the alternative costs —
// two admin secrets is two rotation schedules, and the one nobody remembers to
// rotate is the one that leaks.
//
// THE ONE THING A BROWSER HAS TO GET RIGHT HERE, AND IT IS NOT OBVIOUS.
//
// A 401 carrying `needs_second_factor: true` DOES NOT SPEND THE EMAILED CODE.
// api/src/auth/login.ts says so in as many words and gives the reason: spending
// it would mean a browser that submits an address and a code, learns a factor is
// wanted, and then has nothing left to submit it with — so every sign-in would
// need two mails. The console's obligation is therefore to hold on to the code it
// already has and send it again alongside the six digits. A console that cleared
// the field and asked for the code afresh would turn a working server design into
// two emails per sign-in, and would look like a server bug.
//
// That is the property check.mjs observes by comparing the two request bodies
// rather than by reading this comment: the `code` in the second POST must be
// byte-identical to the one in the first.
//
// AND THE ONE THING IT MUST NOT DO. It must not ask, before or during, whether an
// address has a second factor. There is no endpoint that answers that and there
// must not be: "which of these addresses has two-factor turned on" is a question
// about a client's staff, and an attacker who learns the answer learns which
// mailbox is worth stealing. login.ts's header sets this out at length. The
// consequence for the console is that the first attempt is the same request shape
// for every account in existence — `{email, code}`, with no `totp` key at all —
// and the second factor is discovered from the refusal rather than asked about.
// `totp` is optional in the service's request schema for exactly this reason, and
// omitting it is using that design rather than working around it.

import { AUTH_ENDPOINTS } from "./endpoints.js";
import { unauthenticated, type ApiOptions } from "./client.js";
import type { Session } from "../session/store.js";

/**
 * Where the person is in the flow.
 *
 * `code` is carried on `second_factor` deliberately and it is the state that
 * makes the header's rule expressible. A step that dropped it would leave the
 * next request unable to be constructed, which is the failure written as a type
 * rather than as a comment.
 */
export type SignInStep =
  /** Nothing typed yet, or a code has been asked for and the form is back at the start. */
  | { readonly step: "address" }
  /** A code has been requested for this address. The same address is reused, never retyped. */
  | { readonly step: "code"; readonly email: string }
  /** The mailbox is proved and an authenticator is wanted. Both halves are held. */
  | { readonly step: "second_factor"; readonly email: string; readonly code: string };

export type CodeRequestResult =
  /**
   * Accepted. It says nothing about whether an account exists or a mail was sent,
   * because the server says nothing about either — one frozen body for every
   * address. The console repeats that rather than improving on it.
   */
  | { readonly ok: true; readonly detail: string }
  /** The address is not an address, which the server answers before it looks anything up. */
  | { readonly ok: false; readonly detail: string };

export type SessionResult =
  | { readonly ok: true; readonly session: Session }
  /**
   * Refused, and `wantsSecondFactor` is the single bit of specificity the login
   * path permits.
   *
   * It is safe to name because it is only ever reached by somebody who has
   * already presented the right code for the right address — which is to say by
   * somebody holding the mailbox, who therefore already knows the account
   * exists. login.ts's RedeemOutcome carries the same argument on the server
   * side.
   */
  | { readonly ok: false; readonly wantsSecondFactor: boolean; readonly detail: string };

/**
 * Ask for a code.
 *
 * The address is sent as typed. Normalisation is the server's — codes.ts owns
 * `normaliseEmail` and there is one normalisation — and a console that lowercased
 * or trimmed on its own would be a second implementation of it, which is how the
 * two eventually disagree about an address with a capital letter in it.
 */
export async function requestCode(options: ApiOptions, email: string): Promise<CodeRequestResult> {
  const answer = await unauthenticated(options, AUTH_ENDPOINTS.requestLoginCode, { email });

  if (answer.ok) {
    return {
      ok: true,
      detail:
        said(answer.body) ??
        "If that address has an account, a code is on its way. Asking again sends a new one and closes the old one.",
    };
  }

  if (answer.status === 400) {
    // The only refusal this route gives that is about the request rather than
    // about the world, and the server answers it before the timing floor and
    // before it looks anything up — so passing it straight through leaks nothing.
    // It saves somebody who mistyped their address from waiting ten minutes for a
    // mail that was never addressable.
    return { ok: false, detail: said(answer.body) ?? "That is not an email address." };
  }

  return {
    ok: false,
    detail:
      said(answer.body) ??
      "The console could not reach the service to ask for a code. Nothing has been sent. Try again shortly.",
  };
}

/**
 * Spend a code for a session, with or without an authenticator.
 *
 * `totp` is omitted from the body entirely when there is none, rather than sent
 * as an empty string. The server treats the two identically — `(totp ?? "").trim()
 * === ""` — so this is not about being understood. It is about the request shape
 * on the wire being the same for every account on a first attempt, which is the
 * property the header describes and which check.mjs asserts by looking at the
 * keys of the body that was actually sent.
 */
export async function createSession(
  options: ApiOptions,
  credentials: { email: string; code: string; totp?: string },
): Promise<SessionResult> {
  const totp = credentials.totp?.trim();

  const answer = await unauthenticated(options, AUTH_ENDPOINTS.createSession, {
    email: credentials.email,
    code: credentials.code,
    ...(totp === undefined || totp === "" ? {} : { totp }),
    // ASKING FOR ROTATION, WHICH IS A STATEMENT ABOUT THIS PROGRAM RATHER THAN A
    // PREFERENCE. api/src/auth/routes.ts frames the field as a capability
    // question — can you replace the token you hold when a response tells you to
    // — and the answer here is yes for one specific reason: api/client.ts adopts
    // a replacement on every authenticated request, on the refusals as well as
    // the successes, and there is no other path out of this package that carries
    // an Authorization header.
    //
    // ALWAYS TRUE AND NOT CONFIGURABLE. A flag would be a way for a build to
    // sign in without rotation, which is a security property somebody could turn
    // off from the browser — and the console is not where that is decided. The
    // service decides whether to honour it and echoes back what it did.
    //
    // Unlike `totp`, this key is present on every request including the first,
    // and it does not disturb the property the header describes: the first
    // attempt is still the same shape for every account in existence, because
    // this field is a fact about the console and not about the account.
    rotate_token: true,
  });

  if (answer.ok) {
    const session = asSession(answer.body);
    if (session === null) {
      // A 200 whose body is not a session. Either the console is talking to
      // something that is not the API, or the response shape changed under us.
      // Refused rather than partially believed, because the field that would be
      // missing is the token and a session with no token is a signed-in screen
      // that 401s on its first read.
      return {
        ok: false,
        wantsSecondFactor: false,
        detail:
          "The service accepted the sign-in and did not send back a session this console recognises. " +
          "Nothing is signed in. This is a fault on our side rather than anything you did.",
      };
    }
    // The address goes on here rather than being read out of the response,
    // because the response does not carry one and must not start to. The value
    // is the caller's own argument coming back out: `credentials.email` is what
    // was typed into the form and what was just sent, so this adds no fact the
    // browser did not already hold. store.ts's field carries the rest of the
    // argument, including why the screens call it the address this sign-in used
    // rather than the account's address.
    return { ok: true, session: { ...session, email: credentials.email } };
  }

  // The flag is read only off a 401, which is the only status the service sets it
  // on. Reading it off any refusal would mean a proxy or an error page that
  // happened to contain the words could put the console into a state where it
  // prompts for an authenticator that does not exist.
  const wantsSecondFactor = answer.status === 401 && answer.body?.needs_second_factor === true;

  return {
    ok: false,
    wantsSecondFactor,
    detail:
      said(answer.body) ??
      (answer.status === null
        ? "The console could not reach the service. Nothing is signed in."
        : "That address and code are not a live sign-in. Ask for a new code and try again."),
  };
}

/**
 * The next step after a refusal, which is where the header's rule is enforced.
 *
 * A pure function rather than something the component works out inline, so that
 * "keep the code" has one place to break and so the check can drive every
 * transition without rendering anything.
 *
 * `submittedCode` is taken as an argument rather than read off `current` because
 * the `code` step deliberately does not carry one: while somebody is typing, the
 * code is the form's business. It becomes state worth keeping only at the moment
 * it turns out to have been right, which is exactly the transition below.
 *
 * A refusal that does not want a factor goes back to `code` and NOT to
 * `address`, carrying the address forward. Sending somebody back to retype their
 * address after a mistyped code is how a form gets a reputation, and the address
 * was never the thing in doubt.
 */
export function stepAfterRefusal(
  current: SignInStep,
  submittedCode: string,
  result: Extract<SessionResult, { ok: false }>,
): SignInStep {
  if (current.step === "address") return current;

  if (!result.wantsSecondFactor) {
    return { step: "code", email: current.email };
  }

  if (current.step === "second_factor") {
    // The digits were wrong or replayed. Stay here with both halves intact: the
    // emailed code is still unspent, because login.ts throws out of the
    // transaction rather than committing the consumption, so the person types six
    // new digits and nothing else. Keeping `current.code` rather than
    // `submittedCode` is the point — the latter is the same value, and reading it
    // from the step is what makes a future edit to the form unable to lose it.
    return current;
  }

  return { step: "second_factor", email: current.email, code: submittedCode };
}

/**
 * Whether a refusal is somebody being told no, or somebody being moved along.
 *
 * THE DEFECT THIS EXISTS TO MAKE UNWRITEABLE, AND IT SHIPPED. `createSession`
 * reports the second-factor prompt as `ok: false`, because that is what it is on
 * the wire: a 401 with a sentence on it. The screen then drew every `ok: false`
 * in the box headed "That did not work" — so an account with an authenticator was
 * told its sign-in had failed at the exact moment it had succeeded, and the
 * server's own sentence underneath it ("an authenticator code is needed") read as
 * the reason for a failure rather than as the next thing to do.
 *
 * THE DISTINCTION IS NOT WHETHER THE REQUEST SUCCEEDED. It is whether something
 * new is being asked of the person, and the step is what carries that: a factor
 * wanted while somebody is still on the emailed code is the flow going forward,
 * and the identical flag on the authenticator step is six digits that were wrong.
 * `wantsSecondFactor` alone cannot tell those apart, which is why this takes the
 * step as well and why the component cannot be trusted to work it out inline.
 *
 * IT MOVES FORWARD EXACTLY WHEN THE STEP MOVES TO THE AUTHENTICATOR, which is a
 * property rather than a coincidence: `stepAfterRefusal` above is the only
 * function that decides that, and a plain refusal from the authenticator step
 * moves *back* to the code. That is a step change and a refusal, so "did the
 * step change" is the wrong question. check.mjs drives every transition through
 * both functions and compares, so a future edit that taught one about a new
 * refusal and not the other is red rather than a screen that narrates a step
 * forward as a failure.
 *
 * NOT FIXED BY QUIETENING GENUINE REFUSALS. The authenticator step's own
 * sentence — "those digits are not right" — is the only thing that tells
 * somebody to look at the app again. A console that bought silence on the false
 * alarm by dropping the true ones would have replaced a confusing screen with an
 * inert one.
 */
export type SignInNarration = {
  /** `progress` is not drawn as a failure. `refusal` is. */
  readonly kind: "progress" | "refusal";
  /** The server's sentence, passed through rather than reworded. */
  readonly detail: string;
};

export function narrateRefusal(
  current: SignInStep,
  result: Extract<SessionResult, { ok: false }>,
): SignInNarration {
  // `current.step === "code"` and not merely `wantsSecondFactor`: on the
  // authenticator step the same flag means the digits were wrong, and on the
  // address step nothing has been submitted to refuse.
  const movingOn = result.wantsSecondFactor && current.step === "code";

  return { kind: movingOn ? "progress" : "refusal", detail: result.detail };
}

/**
 * The four stages of signing in, and the states they can be in.
 *
 * `to_come` rather than `skipped`, and there is no `skipped` at all. An account
 * with no authenticator goes from the code straight to a session, and a strip
 * that marked the third stage skipped would be telling somebody they missed
 * something on the one sign-in where nothing went wrong.
 */
export type SignInStageState = "done" | "now" | "to_come";

export type SignInStageId = "address" | "code" | "second_factor" | "open";

export type SignInStage = {
  readonly id: SignInStageId;
  readonly title: string;
  readonly state: SignInStageState;
};

/**
 * The stages themselves: one list, a module constant, the same four for
 * everybody.
 *
 * THE THIRD ONE IS DRAWN TO EVERY ADDRESS, INCLUDING THE ACCOUNTS THAT WILL
 * NEVER REACH IT, and that is the whole design rather than an oversight. The
 * alternative that reads more smoothly — draw the stages this account will
 * actually need — cannot be written: at the moment the strip is first drawn
 * nothing in this program knows whether there is a factor, and the login path is
 * built so that nothing can find out until the emailed code is proved. What could
 * be written is the version that draws two stages for some addresses and three
 * for others as soon as the answer arrives, and that is the leak — see
 * `signInStages` below. The unreached authenticator is still a segment of the
 * bar, not a missing one, so the count cannot become an answer about the account.
 *
 * THE FOURTH IS THE DESTINATION RATHER THAN A FORM. Nothing on the login screen
 * is what it names: the console opening is App.tsx swapping that surface out for
 * the landing screen, which is why `signInStages` never returns `now` for it.
 * Drawn anyway, because "and then you are in" is what tells somebody the emailed
 * code is not the last thing being asked of them. A four-segment bar that filled
 * the fourth segment on this surface would be lying about where somebody is.
 *
 * The titles are the words above the bar, one at a time, as the step moves.
 * They stay on this list so the strip's markup is still four stages with names
 * a screen reader can walk, even though only the current title is shown.
 */
export const SIGN_IN_STAGES: readonly { readonly id: SignInStageId; readonly title: string }[] = [
  { id: "address", title: "Your address" },
  { id: "code", title: "Code" },
  { id: "second_factor", title: "Authenticator" },
  { id: "open", title: "Opens" },
];

/**
 * Which stage is where, given the step.
 *
 * THE SIGNATURE IS THE SECURITY PROPERTY, AND IT IS WORTH READING AS ONE. This
 * takes the step and nothing else. There is no parameter for the account, none
 * for the last refusal, and none for whether a factor was wanted — so there is
 * nothing here that a stage could be computed from except where in the flow
 * somebody is, and the flow is in the same place for every address in existence
 * until the emailed code comes back right. A progress indicator that drew three
 * stages for an account with a factor and two for an account without would
 * announce, before anything had been proved, which addresses are worth
 * attacking. That is the enumeration channel this file's header spends thirty
 * lines refusing to open, and an indicator is the easiest place in the console
 * to open it by accident.
 *
 * So it is not defended by remembering to be careful. It is defended by there
 * being no argument to be careless with, and check.mjs observes the same
 * property from the other side by driving two accounts through the form and
 * comparing the markup the strip renders before the code is spent.
 *
 * `open` is never `now`, per SIGN_IN_STAGES. The furthest this function goes is
 * the authenticator stage, because the surface that would draw the fourth one as
 * current is the surface that has replaced this one.
 */
export function signInStages(step: SignInStep): readonly SignInStage[] {
  const at = SIGN_IN_STAGES.findIndex((stage) => stage.id === step.step);

  return SIGN_IN_STAGES.map(({ id, title }, index) => ({
    id,
    title,
    state: index === at ? "now" : index < at ? "done" : "to_come",
  }));
}

/**
 * True where a response body is a session. Every field checked, none cast.
 *
 * Returns everything but the address, and the type says so rather than leaving
 * it to a comment. `email` is not a field of the response and there is no
 * reading of a body that could produce one, so a signature that promised a
 * whole `Session` would be a signature inviting somebody to look for it here.
 */
function asSession(body: unknown): Omit<Session, "email"> | null {
  if (typeof body !== "object" || body === null) return null;
  const row = body as Record<string, unknown>;

  const token = row["token"];
  const sessionId = row["session_id"];
  const expiresAt = row["expires_at"];
  const role = row["role"];
  const clientId = row["client_id"];
  const rotatesOnUse = row["rotates_on_use"];
  const secondFactorProved = row["second_factor_proved"];

  if (typeof token !== "string" || token === "") return null;
  if (typeof sessionId !== "string" || sessionId === "") return null;
  if (typeof expiresAt !== "string" || Number.isNaN(Date.parse(expiresAt))) return null;
  if (typeof role !== "string" || role === "") return null;
  if (clientId !== null && clientId !== undefined && typeof clientId !== "string") return null;
  // REFUSED WHEN ABSENT, WHICH MEANS THIS CONSOLE WILL NOT SIGN IN AGAINST A
  // SERVICE THAT PREDATES ROTATION — and that is the intended reading rather
  // than an accident of strictness. The two are deployed together, and the
  // alternative default is the one that fails silently: reading a missing field
  // as false would let a console sign in against a service that IS rotating and
  // then ignore every replacement, which ends the session a grace window later
  // with a 401 nobody can explain. A refusal at the door says what happened.
  //
  // It costs nothing that is not already paid: the block above already refuses a
  // 200 whose body is not a session, with a sentence saying so.
  if (typeof rotatesOnUse !== "boolean") return null;
  // THE SAME REFUSAL, FOR THE SAME REASON, ABOUT A DIFFERENT LIE. A 200 that
  // does not say what this session proved is not a session. Reading the absence
  // as false would be the reading routes.ts forbids: "this session proved no
  // factor" presented as a fact about an answer that was never given, which is
  // how the landing screen would tell somebody who used an authenticator that
  // they have none. The two are deployed together; a service that predates the
  // field is not one this console signs in against.
  if (typeof secondFactorProved !== "boolean") return null;
  // NOT REFUSED WHEN ABSENT, unlike the two above. The export switch decides
  // whether Data is offered and nothing else, and the export route reads it
  // again on every request; a body without it is a session whose switch is
  // unknown, which roleAccess.ts draws as off.
  const canExport = row["can_export"];

  return {
    token,
    sessionId,
    expiresAt,
    role,
    clientId: typeof clientId === "string" ? clientId : null,
    rotatesOnUse,
    secondFactorProved,
    canExport: typeof canExport === "boolean" ? canExport : null,
  };
}

/**
 * The `detail` sentence out of a response body, or null.
 *
 * Takes `unknown` rather than a body type, because it is called on a success
 * body as well as on a refusal and the success bodies here are not typed until
 * they have been validated. Narrowing inside is what makes it safe to call on
 * either: a body that is not an object, or an object with no string detail,
 * comes back as null and the caller supplies its own sentence.
 */
function said(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const detail = (body as { detail?: unknown }).detail;
  return typeof detail === "string" && detail !== "" ? detail : null;
}
